//
//  routes.tsx
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module defines routes routing and navigation data.
//

import { lazy } from "react";
import { Navigate, Outlet, ScrollRestoration, type RouteObject } from "react-router-dom";

import type { User } from "src/api/Tmd.dtos";
import {
    DepartmentIdentityBreadcrumb,
    GroupIdentityBreadcrumb,
    InstitutionIdentityBreadcrumb,
    InstitutionFormIdentityBreadcrumb,
    ItemComponentIdentityBreadcrumb,
    PatientIdentityBreadcrumb,
    PatientItemIdentityBreadcrumb,
    PersonnelIdentityBreadcrumb,
    RoleIdentityBreadcrumb,
    TrialFormIdentityBreadcrumb,
    TrialGroupIdentityBreadcrumb,
    TrialIdentityBreadcrumb,
    TrialInstitutionIdentityBreadcrumb,
    TrialInstitutionItemIdentityBreadcrumb,
    TrialInstitutionSupportingSiteIdentityBreadcrumb,
    TrialItemIdentityBreadcrumb
} from "src/components/layouts/breadcrumbs/IdentityBreadcrumbs";
import StaticRouteBreadcrumbs from "src/components/layouts/breadcrumbs/StaticRouteBreadcrumbs";
import { RouteTrialGroupIdentityProvider } from "src/components/providers/data/TrialGroupIdentityProvider";
import DepartmentSubMenu from "src/components/layouts/subMenus/DepartmentSubMenu";
import GeneralSettingsSubMenu from "src/components/layouts/subMenus/GeneralSettingsSubMenu";
import GroupSubMenu from "src/components/layouts/subMenus/GroupSubMenu";
import HubspotSettingsSubMenu from "src/components/layouts/subMenus/HubspotSettingsSubMenu";
import InstitutionSettingsSubMenu from "src/components/layouts/subMenus/InstitutionSettingsSubMenu";
import InstitutionSubMenu from "src/components/layouts/subMenus/InstitutionSubMenu";
import ItemSettingsSubMenu from "src/components/layouts/subMenus/ItemSettingsSubMenu";
import ParticipantSettingsSubMenu from "src/components/layouts/subMenus/ParticipantSettingsSubMenu";
import PatientSubMenu from "src/components/layouts/subMenus/PatientSubMenu";
import PersonnelSubMenu from "src/components/layouts/subMenus/PersonnelSubMenu";
import QaSettingsSubMenu from "src/components/layouts/subMenus/QaSettingsSubMenu";
import RoleSubMenu from "src/components/layouts/subMenus/RoleSubMenu";
import SettingsSubMenu from "src/components/layouts/subMenus/SettingsSubMenu";
import TrialConfigurationSubMenu from "src/components/layouts/subMenus/TrialConfigurationSubMenu";
import TrialFormSubMenu from "src/components/layouts/subMenus/TrialFormSubMenu";
import TrialInstitutionSubMenu from "src/components/layouts/subMenus/TrialInstitutionSubMenu";
import TrialInstitutionSupportingSiteSubMenu from "src/components/layouts/subMenus/TrialInstitutionSupportingSiteSubMenu";
import TrialItemsSubMenu from "src/components/layouts/subMenus/TrialItemsSubMenu";
import TrialSettingsSubMenu from "src/components/layouts/subMenus/TrialSettingsSubMenu";
import TrialSubMenu from "src/components/layouts/subMenus/TrialSubMenu";
import { RouteDepartmentIdentityProvider } from "src/components/providers/data/DepartmentIdentityProvider";
import { RouteGroupIdentityProvider } from "src/components/providers/data/GroupIdentityProvider";
import { RouteInstitutionFormIdentityProvider } from "src/components/providers/data/InstitutionFormIdentityProvider";
import { RouteInstitutionIdentityProvider } from "src/components/providers/data/InstitutionIdentityProvider";
import { RouteItemComponentIdentityProvider } from "src/components/providers/data/ItemComponentIdentityProvider";
import { RoutePatientIdentityProvider } from "src/components/providers/data/PatientIdentityProvider";
import { RoutePatientItemIdentityProvider } from "src/components/providers/data/PatientItemIdentityProvider";
import { RoutePersonnelIdentityProvider } from "src/components/providers/data/PersonnelIdentityProvider";
import { RouteRoleIdentityProvider } from "src/components/providers/data/RoleIdentityProvider";
import { RouteTrialFormIdentityProvider } from "src/components/providers/data/TrialFormIdentityProvider";
import { RouteTrialInstitutionItemIdentityProvider } from "src/components/providers/data/TrialInstitutionItemIdentityProvider";
import { RouteTrialItemIdentityProvider } from "src/components/providers/data/TrialItemIdentityProvider";

import ApplicationLayout from "../components/layouts/ApplicationLayout";
import { RouteTrialIdentityProvider } from "../components/providers/data/TrialIdentityProvider";
import { RouteTrialInstitutionIdentityProvider } from "../components/providers/data/TrialInstitutionIdentityProvider";
import { RouteTrialInstitutionSupportingSiteIdentityProvider } from "../components/providers/data/TrialInstitutionSupportingSiteIdentityProvider";
import LayoutProvider from "../components/providers/layouts/LayoutProvider";
import { defaultLayoutContext, type ILayoutContext } from "../contexts/layouts/LayoutContext";
import { mainNavigationItems } from "./navigation";
import {
    InstitutionDepartmentPageRedirect,
    TrialInstitutionDetailsPageRedirect,
    TrialInstitutionItemPageRedirect,
    TrialInstitutionItemsPageRedirect,
    TrialInstitutionNotePageRedirect,
    TrialInstitutionNotesPageRedirect,
    TrialInstitutionParticipantPageRedirect,
    TrialInstitutionParticipantsPageRedirect,
    TrialInstitutionPersonnelDetailsPageRedirect,
    TrialInstitutionPersonnelPageRedirect,
    TrialInstitutionSupportingSitePageRedirect,
    TrialParticipantPageRedirect,
    TrialConfigurationPageRedirect,
    TrialGroupItemTemplateLegacyRedirect,
    TrialItemEntryPageRedirect,
    TrialItemPageRedirect,
    TrialItemsPageRedirect,
    TrialTreatmentPageRedirect
} from "./redirects";

//import AnalyticsTracker from "../components/common/AnalyticsTracker/AnalyticsTracker";

const TrialsPage = lazy(() => import("src/pages/trials/TrialsPage"));
const TrialPage = lazy(() => import("src/pages/trials/TrialPage"));
const TrialApiPage = lazy(() => import("src/pages/trials/configuration/TrialApiPage"));
const TrialMilestonesPage = lazy(() => import("src/pages/trials/TrialMilestonesPage"));
const TrialCapabilitiesPage = lazy(
    () => import("src/pages/trials/configuration/TrialCapabilitiesPage")
);
const TrialTagsPage = lazy(() => import("src/pages/trials/configuration/TrialTagsPage"));
const TrialEmailsPage = lazy(() => import("src/pages/trials/configuration/TrialEmailsPage"));
const TrialPersonnelEmailPreferencesPage = lazy(
    () => import("src/pages/trials/configuration/TrialPersonnelEmailPreferencesPage")
);
const TrialPatientTagsPage = lazy(
    () => import("src/pages/trials/configuration/TrialPatientTagsPage")
);
const TrialTrackersPage = lazy(() => import("src/pages/trials/trackers/TrialTrackersPage"));
const TrialTrackerPage = lazy(() => import("src/pages/trials/trackers/TrialTrackerPage"));
const TrialNotesPage = lazy(() => import("src/pages/trials/notes/TrialNotesPage"));
const TrialNotePage = lazy(() => import("src/pages/trials/notes/TrialNotePage"));
const TrialConfigurationInstitutionItemTemplatesPage = lazy(
    () =>
        import("src/pages/trials/configuration/institutionItemTemplates/TrialInstitutionItemTemplatesPage")
);
const TrialConfigurationInstitutionItemTemplatePage = lazy(
    () =>
        import("src/pages/trials/configuration/institutionItemTemplates/TrialInstitutionItemTemplatePage")
);
const TrialInstitutionItemTemplateImportPage = lazy(
    () =>
        import("src/pages/trials/configuration/institutionItemTemplates/TrialInstitutionItemTemplateImportPage")
);
const GlobalPatientItemTemplatesPage = lazy(
    () => import("src/pages/settings/items/patientItemTemplates/PatientItemTemplatesPage")
);
const GlobalPatientItemTemplatePage = lazy(
    () => import("src/pages/settings/items/patientItemTemplates/PatientItemTemplatePage")
);
const TrialPatientItemTemplatesPage = lazy(
    () => import("src/pages/trials/configuration/patientItemTemplates/PatientItemTemplatesPage")
);
const TrialPatientItemTemplatePage = lazy(
    () => import("src/pages/trials/configuration/patientItemTemplates/PatientItemTemplatePage")
);
const PatientItemTemplateImportPage = lazy(
    () =>
        import("src/pages/trials/configuration/patientItemTemplates/PatientItemTemplateImportPage")
);
const TrialInstitutionItemBulkUpdatePage = lazy(
    () => import("src/pages/trials/items/TrialInstitutionItemBulkUpdatePage")
);
const TrialGroupItemBulkUpdatePage = lazy(
    () => import("src/pages/trials/items/TrialGroupItemBulkUpdatePage")
);
const TrialParticipantsPage = lazy(
    () => import("src/pages/trials/participants/TrialParticipantsPage")
);
const TrialParticipantPage = lazy(
    () => import("src/pages/trials/participants/TrialParticipantPage")
);
const TrialParticipantAdministrationCaseReportPage = lazy(
    () => import("src/pages/trials/participants/TrialParticipantAdministrationCaseReportPage")
);
const TrialPersonnelPage = lazy(() => import("src/pages/trials/personnel/TrialPersonnelPage"));
const TrialPersonnelCreatePage = lazy(
    () => import("src/pages/trials/personnel/TrialPersonnelCreatePage")
);
const TrialPersonnelDetailsPage = lazy(
    () => import("src/pages/trials/personnel/TrialPersonnelDetailsPage")
);
const TrialGroupsPage = lazy(() => import("src/pages/trials/groups/TrialGroupsPage"));
const TrialGroupPage = lazy(() => import("src/pages/trials/groups/TrialGroupPage"));
const TrialGroupItemsPage = lazy(() => import("src/pages/trials/groups/items/TrialGroupItemsPage"));
const TrialGroupItemPage = lazy(() => import("src/pages/trials/groups/items/TrialGroupItemPage"));
const TrialGroupItemVersionEntryPage = lazy(
    () => import("src/pages/trials/groups/items/TrialGroupItemVersionEntryPage")
);
const TrialGroupSubMenu = lazy(() => import("src/components/layouts/subMenus/TrialGroupSubMenu"));
const GroupConfigurationPage = lazy(() => import("src/pages/groups/GroupConfigurationPage"));
const GroupConfigurationSubMenu = lazy(
    () => import("src/components/layouts/subMenus/GroupConfigurationSubMenu")
);

const TrialTreatmentsPage = lazy(() => import("src/pages/trials/treatments/TrialTreatmentsPage"));
const TrialTreatmentPage = lazy(() => import("src/pages/trials/treatments/TrialTreatmentPage"));
const TrialConfigurationPage = lazy(
    () => import("src/pages/trials/configuration/TrialConfigurationPage")
);
const TrialQaProgramChecklistPage = lazy(
    () => import("src/pages/trials/qaProgramChecklist/TrialQaProgramChecklistPage")
);
const TrialQaProgramChecklistConfigurationPage = lazy(
    () =>
        import("src/pages/trials/configuration/qaProgramChecklist/TrialQaProgramChecklistConfigurationPage")
);
const TrialFormsPage = lazy(() => import("src/pages/trials/forms/TrialFormsPage"));
const TrialFormPage = lazy(() => import("src/pages/trials/forms/TrialFormPage"));
const TrialFormReportPage = lazy(() => import("src/pages/trials/forms/TrialFormReportPage"));
const TrialFormEmailsPage = lazy(() => import("src/pages/trials/forms/TrialFormEmailsPage"));
const TrialFormReportEditPage = lazy(
    () => import("src/pages/trials/forms/TrialFormReportEditPage")
);
const TrialFormAdministrationCaseReportPage = lazy(
    () => import("src/pages/trials/forms/TrialFormAdministrationCaseReportPage")
);
const TrialFormEntryPage = lazy(() => import("src/pages/trials/forms/TrialFormEntryPage"));
const TrialItemsPage = lazy(() => import("src/pages/trials/items/TrialItemsPage"));
const TrialAllItemsPage = lazy(() => import("src/pages/trials/items/TrialAllItemsPage"));
const TrialItemPage = lazy(() => import("src/pages/trials/items/TrialItemPage"));
const TrialItemVersionEntryPage = lazy(
    () => import("src/pages/trials/items/TrialItemVersionEntryPage")
);
const TrialItemImportPage = lazy(() => import("src/pages/trials/items/TrialItemImportPage"));
const TrialItemsNavigationPage = lazy(
    () => import("src/pages/trials/items/TrialItemsNavigationPage")
);
const TrialInstitutionItemsOverviewPage = lazy(
    () => import("src/pages/trials/items/TrialInstitutionItemsOverviewPage")
);
const TrialGroupItemsOverviewPage = lazy(
    () => import("src/pages/trials/items/TrialGroupItemsOverviewPage")
);
const PatientItemsOverviewPage = lazy(
    () => import("src/pages/trials/items/PatientItemsOverviewPage")
);
const PatientItemBulkUpdatePage = lazy(
    () => import("src/pages/trials/items/PatientItemBulkUpdatePage")
);
const PatientItemPage = lazy(() => import("src/pages/trials/participants/items/PatientItemPage"));
const PatientItemVersionEntryPage = lazy(
    () => import("src/pages/trials/participants/items/PatientItemVersionEntryPage")
);
const TrialInstitutionsPage = lazy(
    () => import("src/pages/trials/institutions/TrialInstitutionsPage")
);
const TrialInstitutionPage = lazy(
    () => import("src/pages/trials/institutions/TrialInstitutionPage")
);
const TrialInstitutionCapabilitiesPage = lazy(
    () => import("src/pages/trials/institutions/TrialInstitutionCapabilitiesPage")
);
const TrialInstitutionEmailsPage = lazy(
    () => import("src/pages/trials/institutions/emails/TrialInstitutionEmailsPage")
);
const TrialParticipantEmailsPage = lazy(
    () => import("src/pages/trials/participants/emails/TrialParticipantEmailsPage")
);
const TrialInstitutionSupportingSitesPage = lazy(
    () =>
        import("src/pages/trials/institutions/supportingSites/TrialInstitutionSupportingSitesPage")
);
const TrialInstitutionSupportingSitePage = lazy(
    () => import("src/pages/trials/institutions/supportingSites/TrialInstitutionSupportingSitePage")
);
const TrialInstitutionSupportingSitePersonnelPage = lazy(
    () =>
        import("src/pages/trials/institutions/supportingSites/personnel/TrialInstitutionSupportingSitePersonnelPage")
);
const TrialInstitutionSupportingSitePersonnelCreatePage = lazy(
    () =>
        import("src/pages/trials/institutions/supportingSites/personnel/TrialInstitutionSupportingSitePersonnelCreatePage")
);
const TrialInstitutionSupportingSitePersonnelEditPage = lazy(
    () =>
        import("src/pages/trials/institutions/supportingSites/personnel/TrialInstitutionSupportingSitePersonnelEditPage")
);
const TrialInstitutionParticipantPage = lazy(
    () => import("src/pages/trials/institutions/participants/TrialInstitutionParticipantPage")
);
const TrialInstitutionParticipantsPage = lazy(
    () => import("src/pages/trials/institutions/participants/TrialInstitutionParticipantsPage")
);
const TrialInstitutionItemsPage = lazy(
    () => import("src/pages/trials/institutions/items/TrialInstitutionItemsPage")
);
const TrialInstitutionItemPage = lazy(
    () => import("src/pages/trials/institutions/items/TrialInstitutionItemPage")
);
const TrialInstitutionItemVersionEntryPage = lazy(
    () => import("src/pages/trials/institutions/items/TrialInstitutionItemVersionEntryPage")
);
const TrialInstitutionPersonnelPage = lazy(
    () => import("src/pages/trials/institutions/personnel/TrialInstitutionPersonnelPage")
);
const TrialInstitutionPersonnelCreatePage = lazy(
    () => import("src/pages/trials/institutions/personnel/TrialInstitutionPersonnelCreatePage")
);
const TrialInstitutionPersonnelDetailsPage = lazy(
    () => import("src/pages/trials/institutions/personnel/TrialInstitutionPersonnelDetailsPage")
);
const TrialInstitutionNotesPage = lazy(
    () => import("src/pages/trials/institutions/notes/TrialInstitutionNotesPage")
);
const TrialInstitutionNotePage = lazy(
    () => import("src/pages/trials/institutions/notes/TrialInstitutionNotePage")
);
const InstitutionsPage = lazy(() => import("src/pages/institutions/InstitutionsPage"));
const InstitutionPage = lazy(() => import("src/pages/institutions/InstitutionPage"));
const InstitutionFormsPage = lazy(
    () => import("src/pages/institutions/forms/InstitutionFormsPage")
);
const InstitutionEmailsPage = lazy(
    () => import("src/pages/institutions/emails/InstitutionEmailsPage")
);
const InstitutionFormPage = lazy(() => import("src/pages/institutions/forms/InstitutionFormPage"));
const InstitutionFormEntryPage = lazy(
    () => import("src/pages/institutions/forms/InstitutionFormEntryPage")
);
const InstitutionNotesPage = lazy(
    () => import("src/pages/institutions/notes/InstitutionNotesPage")
);
const InstitutionNotePage = lazy(() => import("src/pages/institutions/notes/InstitutionNotePage"));
const InstitutionPersonnelPage = lazy(
    () => import("src/pages/institutions/personnel/InstitutionPersonnelPage")
);
const InstitutionPersonnelEditPage = lazy(
    () => import("src/pages/institutions/personnel/InstitutionPersonnelEditPage")
);
const InstitutionPersonnelCreatePage = lazy(
    () => import("src/pages/institutions/personnel/InstitutionPersonnelCreatePage")
);
const InstitutionDepartmentsPage = lazy(
    () => import("src/pages/institutions/departments/InstitutionDepartmentsPage")
);
const InstitutionDepartmentEditPage = lazy(
    () => import("src/pages/institutions/departments/InstitutionDepartmentEditPage")
);
const InstitutionDepartmentPersonnelEditPage = lazy(
    () => import("src/pages/institutions/departments/InstitutionDepartmentPersonnelEditPage")
);
const InstitutionDepartmentPersonnelCreatePage = lazy(
    () => import("src/pages/institutions/departments/InstitutionDepartmentPersonnelCreatePage")
);
const GroupsPage = lazy(() => import("src/pages/groups/GroupsPage"));
const GroupPage = lazy(() => import("src/pages/groups/GroupPage"));
const GroupNotesPage = lazy(() => import("src/pages/groups/notes/GroupNotesPage"));
const GroupNotePage = lazy(() => import("src/pages/groups/notes/GroupNotePage"));
const GroupPersonnelPage = lazy(() => import("src/pages/groups/personnel/GroupPersonnelPage"));
const GroupPersonnelCreatePage = lazy(
    () => import("src/pages/groups/personnel/GroupPersonnelCreatePage")
);
const GroupPersonnelEditPage = lazy(
    () => import("src/pages/groups/personnel/GroupPersonnelEditPage")
);
const GroupTrialsPage = lazy(() => import("src/pages/groups/trials/GroupTrialsPage"));
const TrialGroupItemTemplatesPage = lazy(
    () => import("src/pages/groups/items/TrialGroupItemTemplatesPage")
);
const TrialGroupItemTemplatePage = lazy(
    () => import("src/pages/groups/items/TrialGroupItemTemplatePage")
);
const TrialGroupItemTemplateImportPage = lazy(
    () => import("src/pages/groups/items/TrialGroupItemTemplateImportPage")
);
const PersonnelPage = lazy(() => import("src/pages/personnel/PersonnelPage"));
const PersonnelByRolesPage = lazy(() => import("src/pages/personnel/roles/PersonnelByRolesPage"));
const PersonnelDetailsPage = lazy(() => import("src/pages/personnel/PersonnelDetailsPage"));
const PersonnelNotesPage = lazy(() => import("src/pages/personnel/notes/PersonnelNotesPage"));
const PersonnelNotePage = lazy(() => import("src/pages/personnel/notes/PersonnelNotePage"));
const PersonnelRolesPage = lazy(() => import("src/pages/personnel/roles/PersonnelRolesPage"));
const PersonnelRoleEditPage = lazy(() => import("src/pages/personnel/roles/PersonnelRoleEditPage"));
const PersonnelActiveDirectoryPage = lazy(
    () => import("src/pages/personnel/activeDirectory/PersonnelActiveDirectoryPage")
);
const PersonnelOCRolesPage = lazy(
    () => import("src/pages/personnel/openClinica/PersonnelOCRolesPage")
);
const PersonnelPermissionsPage = lazy(
    () => import("src/pages/personnel/permissions/PersonnelPermissionsPage")
);
const PersonnelFilesPage = lazy(() => import("src/pages/personnel/files/PersonnelFilesPage"));
const PersonnelHubspotPage = lazy(() => import("src/pages/personnel/hubspot/PersonnelHubspotPage"));
const PersonnelEmailPreferencesPage = lazy(
    () => import("src/pages/personnel/emails/PersonnelEmailPreferencesPage")
);
const CountriesPage = lazy(() => import("src/pages/settings/general/countries/CountriesPage"));
const CountryPage = lazy(() => import("src/pages/settings/general/countries/CountryPage"));
const StatesPage = lazy(() => import("src/pages/settings/general/states/StatesPage"));
const StatePage = lazy(() => import("src/pages/settings/general/states/StatePage"));
const ItemComponentTypesPage = lazy(
    () => import("src/pages/settings/general/itemComponentTypes/ItemComponentTypesPage")
);
const ItemComponentTypePage = lazy(
    () => import("src/pages/settings/general/itemComponentTypes/ItemComponentTypePage")
);
const NoteTypesPage = lazy(() => import("src/pages/settings/general/noteTypes/NoteTypesPage"));
const NoteTypePage = lazy(() => import("src/pages/settings/general/noteTypes/NoteTypePage"));
const GendersPage = lazy(() => import("src/pages/settings/general/genders/GendersPage"));
const GenderPage = lazy(() => import("src/pages/settings/general/genders/GenderPage"));
const InterestsPage = lazy(() => import("src/pages/settings/general/interests/InterestsPage"));
const InterestPage = lazy(() => import("src/pages/settings/general/interests/InterestPage"));
const MembershipTypesPage = lazy(
    () => import("src/pages/settings/general/membershipTypes/MembershipTypesPage")
);
const MembershipTypePage = lazy(
    () => import("src/pages/settings/general/membershipTypes/MembershipTypePage")
);
const ProfessionalDisciplinesPage = lazy(
    () => import("src/pages/settings/general/professionalDisciplines/ProfessionalDisciplinesPage")
);
const ProfessionalDisciplinePage = lazy(
    () => import("src/pages/settings/general/professionalDisciplines/ProfessionalDisciplinePage")
);
const SalutationsPage = lazy(
    () => import("src/pages/settings/general/salutations/SalutationsPage")
);
const SalutationPage = lazy(() => import("src/pages/settings/general/salutations/SalutationPage"));
const RolesPage = lazy(() => import("src/pages/settings/general/roles/RolesPage"));
const RolePage = lazy(() => import("src/pages/settings/general/roles/RolePage"));
const RolePermissionsPage = lazy(
    () => import("src/pages/settings/general/roles/RolePermissionsPage")
);
const RolePermissionPage = lazy(
    () => import("src/pages/settings/general/roles/RolePermissionPage")
);
const RolePermissionsCreatePage = lazy(
    () => import("src/pages/settings/general/roles/RolePermissionsCreatePage")
);
const NoticesPage = lazy(() => import("src/pages/settings/general/notices/NoticesPage"));
const NoticePage = lazy(() => import("src/pages/settings/general/notices/NoticePage"));
const HelpCoveragePage = lazy(() => import("src/pages/settings/general/help/HelpCoveragePage"));
const GlobalEmailDefaultsPage = lazy(
    () => import("src/pages/settings/general/emails/GlobalEmailDefaultsPage")
);
const HubspotActiveMembersPage = lazy(
    () => import("src/pages/settings/hubspot/HubspotActiveMembersPage")
);
const HubspotExportPage = lazy(() => import("src/pages/settings/hubspot/HubspotExportPage"));
const HubspotMemberPage = lazy(() => import("src/pages/settings/hubspot/HubspotMemberPage"));
const OpenClinicaUserRolesPage = lazy(
    () => import("src/pages/settings/openclinica/OpenClinicaUserRolesPage")
);
const SiteLocationsPage = lazy(
    () => import("src/pages/settings/institution/siteLocations/SiteLocationsPage")
);
const SiteLocationPage = lazy(
    () => import("src/pages/settings/institution/siteLocations/SiteLocationPage")
);
const FacilityAllianceMembersPage = lazy(
    () =>
        import("src/pages/settings/institution/facilityAllianceMembers/FacilityAllianceMembersPage")
);
const FacilityAllianceMemberPage = lazy(
    () =>
        import("src/pages/settings/institution/facilityAllianceMembers/FacilityAllianceMemberPage")
);
const InstitutionTypesPage = lazy(
    () => import("src/pages/settings/institution/institutionTypes/InstitutionTypesPage")
);
const InstitutionTypePage = lazy(
    () => import("src/pages/settings/institution/institutionTypes/InstitutionTypePage")
);
const InstitutionCapabilitiesPage = lazy(
    () => import("src/pages/settings/institutions/InstitutionCapabilitiesPage")
);
const InstitutionCapabilityPage = lazy(
    () => import("src/pages/settings/institutions/InstitutionCapabilityPage")
);
const InstitutionCapabilityCategoriesPage = lazy(
    () => import("src/pages/settings/institutions/InstitutionCapabilityCategoriesPage")
);
const InstitutionCapabilityCategoryPage = lazy(
    () => import("src/pages/settings/institutions/InstitutionCapabilityCategoryPage")
);
const InstitutionTagsPage = lazy(
    () => import("src/pages/settings/institutions/InstitutionTagsPage")
);
const InstitutionTagPage = lazy(() => import("src/pages/settings/institutions/InstitutionTagPage"));
const InstitutionTagCategoriesPage = lazy(
    () => import("src/pages/settings/institutions/InstitutionTagCategoriesPage")
);
const InstitutionTagCategoryPage = lazy(
    () => import("src/pages/settings/institutions/InstitutionTagCategoryPage")
);
const PatientTagCategoriesPage = lazy(
    () => import("src/pages/settings/participants/PatientTagCategoriesPage")
);
const PatientTagCategoryPage = lazy(
    () => import("src/pages/settings/participants/PatientTagCategoryPage")
);
const PatientTagsPage = lazy(() => import("src/pages/settings/participants/PatientTagsPage"));
const PatientTagPage = lazy(() => import("src/pages/settings/participants/PatientTagPage"));
const InstitutionCodingsPage = lazy(
    () => import("src/pages/settings/institution/institutionCodings/InstitutionCodingsPage")
);
const InstitutionCodingPage = lazy(
    () => import("src/pages/settings/institution/institutionCodings/InstitutionCodingPage")
);
const TrialCategoriesPage = lazy(
    () => import("src/pages/settings/trial/trialCategories/TrialCategoriesPage")
);
const TrialCategoryPage = lazy(
    () => import("src/pages/settings/trial/trialCategories/TrialCategoryPage")
);
const TrialTypesPage = lazy(() => import("src/pages/settings/trial/trialTypes/TrialTypesPage"));
const TrialTypePage = lazy(() => import("src/pages/settings/trial/trialTypes/TrialTypePage"));
const TrialPhasesPage = lazy(() => import("src/pages/settings/trial/trialPhases/TrialPhasesPage"));
const TrialPhasePage = lazy(() => import("src/pages/settings/trial/trialPhases/TrialPhasePage"));
const TumourGroupsPage = lazy(
    () => import("src/pages/settings/trial/tumourGroups/TumourGroupsPage")
);
const TumourGroupPage = lazy(() => import("src/pages/settings/trial/tumourGroups/TumourGroupPage"));
const TrialPresentationTypesPage = lazy(
    () => import("src/pages/settings/trial/trialPresentationTypes/TrialPresentationTypesPage")
);
const TrialPresentationTypePage = lazy(
    () => import("src/pages/settings/trial/trialPresentationTypes/TrialPresentationTypePage")
);
const TrialPublicationTypesPage = lazy(
    () => import("src/pages/settings/trial/trialPublicationTypes/TrialPublicationTypesPage")
);
const TrialPublicationTypePage = lazy(
    () => import("src/pages/settings/trial/trialPublicationTypes/TrialPublicationTypePage")
);
const TrialAnalysisTypesPage = lazy(
    () => import("src/pages/settings/trial/trialAnalysisTypes/TrialAnalysisTypesPage")
);
const TrialAnalysisTypePage = lazy(
    () => import("src/pages/settings/trial/trialAnalysisTypes/TrialAnalysisTypePage")
);
const InvolvementsPage = lazy(
    () => import("src/pages/settings/trial/involvements/InvolvementsPage")
);
const InvolvementPage = lazy(() => import("src/pages/settings/trial/involvements/InvolvementPage"));
const AllocatedWPsPage = lazy(
    () => import("src/pages/settings/trial/allocatedWPs/AllocatedWPsPage")
);
const AllocatedWPPage = lazy(() => import("src/pages/settings/trial/allocatedWPs/AllocatedWPPage"));
const FormTemplatesPage = lazy(
    () => import("src/pages/settings/qa/formTemplates/FormTemplatesPage")
);
const FormTemplatePage = lazy(() => import("src/pages/settings/qa/formTemplates/FormTemplatePage"));
const QaProgramChecklistPage = lazy(
    () => import("src/pages/settings/qa/programChecklist/QaProgramChecklistPage")
);
const QAEmailTemplatesPage = lazy(
    () => import("src/pages/settings/qa/emailTemplates/QAEmailTemplatesPage")
);
const QAEmailTemplatePage = lazy(
    () => import("src/pages/settings/qa/emailTemplates/QAEmailTemplatePage")
);
const QAItemComponentsPage = lazy(
    () => import("src/pages/settings/qa/itemComponents/QAItemComponentsPage")
);
const QAItemComponentPage = lazy(
    () => import("src/pages/settings/qa/itemComponents/QAItemComponentPage")
);
const ReportingTagCategoriesPage = lazy(
    () => import("src/pages/settings/qa/reportingTagCategories/ReportingTagCategoriesPage")
);
const ReportingTagCategoryPage = lazy(
    () => import("src/pages/settings/qa/reportingTagCategories/ReportingTagCategoryPage")
);
const ReportingTagsPage = lazy(
    () => import("src/pages/settings/qa/reportingTags/ReportingTagsPage")
);
const ReportingTagPage = lazy(() => import("src/pages/settings/qa/reportingTags/ReportingTagPage"));
const TaskTypesPage = lazy(() => import("src/pages/settings/qa/taskTypes/TaskTypesPage"));
const TaskTypePage = lazy(() => import("src/pages/settings/qa/taskTypes/TaskTypePage"));
const TrackerTypesPage = lazy(() => import("src/pages/settings/qa/trackerTypes/TrackerTypesPage"));
const TrackerTypePage = lazy(() => import("src/pages/settings/qa/trackerTypes/TrackerTypePage"));
const TrackerMilestonesPage = lazy(
    () => import("src/pages/settings/qa/trackerMilestones/TrackerMilestonesPage")
);
const TrackerMilestonePage = lazy(
    () => import("src/pages/settings/qa/trackerMilestones/TrackerMilestonePage")
);
const FormTemplateFileTypesPage = lazy(
    () => import("src/pages/settings/qa/formTemplateFileTypes/FormTemplateFileTypesPage")
);
const FormTemplateFileTypePage = lazy(
    () => import("src/pages/settings/qa/formTemplateFileTypes/FormTemplateFileTypePage")
);
const ItemComponentsPage = lazy(
    () => import("src/pages/settings/items/itemComponents/GeneralItemComponentsPage")
);
const ItemComponentPage = lazy(
    () => import("src/pages/settings/items/itemComponents/GeneralItemComponentPage")
);
const CategoriesPage = lazy(() => import("src/pages/settings/items/categories/CategoriesPage"));
const CategoryPage = lazy(() => import("src/pages/settings/items/categories/CategoryPage"));
const TrialInstitutionItemTemplatesPage = lazy(
    () =>
        import("src/pages/settings/items/trialInstitutionItemTemplates/TrialInstitutionItemTemplatesPage")
);
const TrialInstitutionItemTemplatePage = lazy(
    () =>
        import("src/pages/settings/items/trialInstitutionItemTemplates/TrialInstitutionItemTemplatePage")
);
const TrialItemTemplatesPage = lazy(
    () => import("src/pages/settings/items/trialItemTemplates/TrialItemTemplatesPage")
);
const TrialItemTemplatePage = lazy(
    () => import("src/pages/settings/items/trialItemTemplates/TrialItemTemplatePage")
);
const DashboardPage = lazy(() => import("src/pages/DashboardPage"));
const ErrorPage = lazy(() => import("src/pages/ErrorPage"));
const EmailLogPreviewPage = lazy(() => import("src/pages/emails/EmailLogPreviewPage"));
const HelpPage = lazy(() => import("src/pages/help/HelpPage"));
const SettingsNavigationPage = lazy(() => import("src/pages/settings/SettingsNavigationPage"));
const InstitutionDepartmentPersonnelPage = lazy(
    () => import("src/pages/institutions/departments/InstitutionDepartmentPersonnelPage")
);

/**
 * Builds the trial route subtree with its existing page, provider and adapter nesting.
 *
 * Postconditions: Returns fresh route objects without navigating or loading lazy page modules.
 * The user argument is currently unused; route construction does not filter access by user.
 */
const createTrialRoutes = (_user?: User): RouteObject[] => {
    const routes: RouteObject[] = [
        {
            path: "trials",
            children: [
                {
                    index: true,
                    element: <TrialsPage />
                },
                {
                    path: "create",
                    element: <TrialPage />
                },
                {
                    path: ":trialId",
                    element: (
                        <RouteTrialIdentityProvider>
                            <TrialIdentityBreadcrumb />
                            <TrialSubMenu />
                            <Outlet />
                        </RouteTrialIdentityProvider>
                    ),
                    children: [
                        {
                            path: "details",
                            element: <TrialPage />
                        },
                        {
                            path: "api",
                            element: <TrialConfigurationPageRedirect destination="api" />
                        },
                        {
                            path: "form-settings",
                            element: (
                                <Navigate
                                    to="../forms"
                                    replace
                                />
                            )
                        },
                        {
                            path: "milestones",
                            element: <TrialMilestonesPage />
                        },
                        {
                            path: "configuration",
                            element: (
                                <>
                                    <TrialConfigurationSubMenu />
                                    <Outlet />
                                </>
                            ),
                            children: [
                                {
                                    index: true,
                                    element: <TrialConfigurationPage />
                                },
                                {
                                    path: "qa-program-checklist",
                                    element: <TrialQaProgramChecklistConfigurationPage />
                                },
                                {
                                    path: "capabilities",
                                    element: <TrialCapabilitiesPage />
                                },
                                {
                                    path: "emails",
                                    element: <TrialEmailsPage />
                                },
                                {
                                    path: "emails/preferences",
                                    element: <TrialPersonnelEmailPreferencesPage />
                                },
                                {
                                    path: "tags",
                                    element: <TrialTagsPage />
                                },
                                {
                                    path: "participant-tags",
                                    element: <TrialPatientTagsPage />
                                },
                                {
                                    path: "api",
                                    element: <TrialApiPage />
                                },
                                {
                                    path: "institution-item-templates",
                                    children: [
                                        {
                                            index: true,
                                            element: (
                                                <TrialConfigurationInstitutionItemTemplatesPage />
                                            )
                                        },
                                        {
                                            path: "import",
                                            element: <TrialInstitutionItemTemplateImportPage />
                                        },
                                        {
                                            path: "update",
                                            element: (
                                                <TrialItemsPageRedirect destination="institution-items/update" />
                                            )
                                        },
                                        {
                                            path: ":trialInstitutionItemTemplateId",
                                            element: (
                                                <TrialConfigurationInstitutionItemTemplatePage />
                                            )
                                        }
                                    ]
                                },
                                {
                                    path: "group-item-templates",
                                    children: [
                                        {
                                            index: true,
                                            element: <TrialGroupItemTemplatesPage scope="trial" />
                                        },
                                        {
                                            path: "import",
                                            element: (
                                                <TrialGroupItemTemplateImportPage scope="trial" />
                                            )
                                        },
                                        {
                                            path: "create",
                                            element: <TrialGroupItemTemplatePage scope="trial" />
                                        },
                                        {
                                            path: ":trialGroupItemTemplateId",
                                            element: <TrialGroupItemTemplatePage scope="trial" />
                                        }
                                    ]
                                },
                                {
                                    path: "participant-item-templates",
                                    children: [
                                        { index: true, element: <TrialPatientItemTemplatesPage /> },
                                        {
                                            path: "create",
                                            element: <TrialPatientItemTemplatePage />
                                        },
                                        {
                                            path: "import",
                                            element: <PatientItemTemplateImportPage />
                                        },
                                        {
                                            path: ":patientItemTemplateId",
                                            element: <TrialPatientItemTemplatePage />
                                        }
                                    ]
                                },
                                {
                                    path: "treatments",
                                    children: [
                                        {
                                            index: true,
                                            element: <TrialTreatmentPageRedirect />
                                        },
                                        {
                                            path: ":trialTreatmentId",
                                            element: <TrialTreatmentPageRedirect />
                                        }
                                    ]
                                }
                            ]
                        },
                        {
                            path: "capabilities",
                            element: <TrialConfigurationPageRedirect destination="capabilities" />
                        },
                        {
                            path: "tags",
                            element: <TrialConfigurationPageRedirect destination="tags" />
                        },
                        {
                            path: "trackers",
                            children: [
                                {
                                    index: true,
                                    element: <TrialTrackersPage />
                                },
                                {
                                    path: ":trialTrackerId",
                                    element: <TrialTrackerPage />
                                }
                            ]
                        },
                        {
                            path: "qa-program-checklist",
                            element: <TrialQaProgramChecklistPage />
                        },
                        {
                            path: "notes",
                            children: [
                                {
                                    index: true,
                                    element: <TrialNotesPage />
                                },
                                {
                                    path: ":noteId",
                                    element: <TrialNotePage />
                                }
                            ]
                        },
                        {
                            path: "trialitems",
                            children: [
                                {
                                    index: true,
                                    element: (
                                        <TrialConfigurationPageRedirect destination="institution-item-templates" />
                                    )
                                },
                                {
                                    path: ":trialItemId",
                                    element: (
                                        <TrialConfigurationPageRedirect
                                            destination="institution-item-templates"
                                            recordParameter="trialItemId"
                                        />
                                    )
                                }
                            ]
                        },
                        {
                            path: "trialitemimport",
                            element: (
                                <TrialConfigurationPageRedirect destination="institution-item-templates/import" />
                            )
                        },
                        {
                            path: "trialitemupdate",
                            element: (
                                <TrialItemsPageRedirect destination="institution-items/update" />
                            )
                        },
                        {
                            path: "participants",
                            children: [
                                {
                                    index: true,
                                    element: <TrialParticipantsPage />
                                },
                                {
                                    path: "create",
                                    element: <TrialParticipantPage />
                                },
                                {
                                    path: ":patientId",
                                    element: (
                                        <RoutePatientIdentityProvider>
                                            <PatientIdentityBreadcrumb />
                                            <PatientSubMenu />
                                            <Outlet />
                                        </RoutePatientIdentityProvider>
                                    ),
                                    children: [
                                        {
                                            index: true,
                                            element: <TrialParticipantPageRedirect />
                                        },
                                        {
                                            path: "details",
                                            element: <TrialParticipantPage />
                                        },
                                        {
                                            path: "administrationcasereport",
                                            element: (
                                                <TrialParticipantAdministrationCaseReportPage />
                                            )
                                        },
                                        {
                                            path: "emails",
                                            element: <TrialParticipantEmailsPage />
                                        },
                                        {
                                            path: "items",
                                            children: [
                                                {
                                                    index: true,
                                                    element: <PatientItemsOverviewPage />
                                                },
                                                { path: "create", element: <PatientItemPage /> },
                                                {
                                                    path: ":patientItemId",
                                                    element: (
                                                        <RoutePatientItemIdentityProvider>
                                                            <PatientItemIdentityBreadcrumb />
                                                            <Outlet />
                                                        </RoutePatientItemIdentityProvider>
                                                    ),
                                                    children: [
                                                        {
                                                            index: true,
                                                            element: <PatientItemPage />
                                                        },
                                                        {
                                                            path: "entry",
                                                            element: <PatientItemVersionEntryPage />
                                                        },
                                                        {
                                                            path: "versions/:patientItemVersionId/entry",
                                                            element: <PatientItemVersionEntryPage />
                                                        }
                                                    ]
                                                }
                                            ]
                                        }
                                    ]
                                }
                            ]
                        },
                        {
                            path: "personnel",
                            children: [
                                {
                                    index: true,
                                    element: <TrialPersonnelPage />
                                },
                                {
                                    path: "create",
                                    element: <TrialPersonnelCreatePage />
                                },
                                {
                                    path: ":personnelRoleId",
                                    element: <TrialPersonnelDetailsPage />
                                }
                            ]
                        },
                        {
                            path: "groups",
                            children: [
                                {
                                    index: true,
                                    element: <TrialGroupsPage />
                                },
                                {
                                    path: ":trialGroupId",
                                    element: (
                                        <RouteTrialGroupIdentityProvider>
                                            <TrialGroupIdentityBreadcrumb />
                                            <TrialGroupSubMenu />
                                            <Outlet />
                                        </RouteTrialGroupIdentityProvider>
                                    ),
                                    children: [
                                        {
                                            index: true,
                                            element: (
                                                <Navigate
                                                    to="details"
                                                    replace
                                                />
                                            )
                                        },
                                        { path: "details", element: <TrialGroupPage /> },
                                        { path: "items", element: <TrialGroupItemsPage /> },
                                        {
                                            path: "items/:trialGroupItemId/details",
                                            element: <TrialGroupItemPage />
                                        },
                                        {
                                            path: "items/:trialGroupItemId/entry",
                                            element: <TrialGroupItemVersionEntryPage />
                                        },
                                        {
                                            path: "items/:trialGroupItemId/versions/:trialGroupItemVersionId/entry",
                                            element: <TrialGroupItemVersionEntryPage />
                                        }
                                    ]
                                }
                            ]
                        },
                        {
                            path: "treatments",
                            children: [
                                {
                                    index: true,
                                    element: <TrialTreatmentsPage />
                                },
                                {
                                    path: ":trialTreatmentId",
                                    element: <TrialTreatmentPage />
                                }
                            ]
                        },
                        {
                            path: "forms",
                            children: [
                                {
                                    index: true,
                                    element: <TrialFormsPage />
                                },
                                {
                                    path: ":trialFormId",
                                    element: (
                                        <RouteTrialFormIdentityProvider>
                                            <TrialFormIdentityBreadcrumb />
                                            <TrialFormSubMenu />
                                            <Outlet />
                                        </RouteTrialFormIdentityProvider>
                                    ),
                                    children: [
                                        {
                                            index: true,
                                            element: <TrialFormPage />
                                        },
                                        {
                                            path: "report",
                                            children: [
                                                {
                                                    index: true,
                                                    element: <TrialFormReportPage />
                                                },
                                                {
                                                    path: "edit",
                                                    element: <TrialFormReportEditPage />
                                                }
                                            ]
                                        },
                                        {
                                            path: "administrationcasereport",
                                            element: <TrialFormAdministrationCaseReportPage />
                                        },
                                        {
                                            path: "emails",
                                            element: <TrialFormEmailsPage />
                                        },
                                        {
                                            path: "entry",
                                            element: <TrialFormEntryPage />
                                        },
                                        {
                                            path: "versions/:trialFormVersionId/entry",
                                            element: <TrialFormEntryPage />
                                        }
                                    ]
                                }
                            ]
                        },
                        {
                            path: "items",
                            element: (
                                <>
                                    <TrialItemsSubMenu />
                                    <Outlet />
                                </>
                            ),
                            children: [
                                {
                                    index: true,
                                    element: <TrialItemsNavigationPage />
                                },
                                { path: "all-items", element: <TrialAllItemsPage /> },
                                {
                                    path: "trial-items",
                                    children: [
                                        {
                                            index: true,
                                            element: <TrialItemsPage />
                                        },
                                        {
                                            path: "create",
                                            element: <TrialItemPage />
                                        },
                                        {
                                            path: "import",
                                            element: <TrialItemImportPage />
                                        },
                                        {
                                            path: ":trialItemId",
                                            element: (
                                                <RouteTrialItemIdentityProvider>
                                                    <TrialItemIdentityBreadcrumb />
                                                    <Outlet />
                                                </RouteTrialItemIdentityProvider>
                                            ),
                                            children: [
                                                {
                                                    index: true,
                                                    element: <TrialItemPage />
                                                },
                                                {
                                                    path: "entry",
                                                    element: <TrialItemVersionEntryPage />
                                                },
                                                {
                                                    path: "versions/:trialItemVersionId/entry",
                                                    element: <TrialItemVersionEntryPage />
                                                }
                                            ]
                                        }
                                    ]
                                },
                                {
                                    path: "institution-items",
                                    children: [
                                        {
                                            index: true,
                                            element: <TrialInstitutionItemsOverviewPage />
                                        },
                                        {
                                            path: "update",
                                            element: <TrialInstitutionItemBulkUpdatePage />
                                        }
                                    ]
                                },
                                {
                                    path: "group-items",
                                    children: [
                                        { index: true, element: <TrialGroupItemsOverviewPage /> },
                                        {
                                            path: "update",
                                            element: <TrialGroupItemBulkUpdatePage />
                                        }
                                    ]
                                },
                                {
                                    path: "participant-items",
                                    children: [
                                        { index: true, element: <PatientItemsOverviewPage /> },
                                        { path: "update", element: <PatientItemBulkUpdatePage /> }
                                    ]
                                },
                                {
                                    path: "create",
                                    element: (
                                        <TrialItemsPageRedirect destination="trial-items/create" />
                                    )
                                },
                                {
                                    path: ":trialItemId",
                                    children: [
                                        {
                                            index: true,
                                            element: <TrialItemPageRedirect />
                                        },
                                        {
                                            path: "entry",
                                            element: <TrialItemEntryPageRedirect />
                                        },
                                        {
                                            path: "versions/:trialItemVersionId/entry",
                                            element: <TrialItemPageRedirect />
                                        }
                                    ]
                                }
                            ]
                        },
                        {
                            path: "itemsimport",
                            element: <TrialItemsPageRedirect destination="trial-items/import" />
                        },
                        {
                            path: "institutions",
                            children: [
                                {
                                    index: true,
                                    element: <TrialInstitutionsPage />
                                },
                                {
                                    path: "create",
                                    element: <TrialInstitutionPage />
                                },
                                {
                                    path: ":trialInstitutionId",
                                    element: (
                                        <RouteTrialInstitutionIdentityProvider>
                                            <TrialInstitutionIdentityBreadcrumb />
                                            <TrialInstitutionSubMenu />
                                            <Outlet />
                                        </RouteTrialInstitutionIdentityProvider>
                                    ),
                                    children: [
                                        {
                                            path: "details",
                                            element: <TrialInstitutionPage />
                                        },
                                        {
                                            path: "supporting-sites",
                                            children: [
                                                {
                                                    index: true,
                                                    element: <TrialInstitutionSupportingSitesPage />
                                                },
                                                {
                                                    path: "create",
                                                    element: <TrialInstitutionSupportingSitePage />
                                                },
                                                {
                                                    path: ":supportingSiteId",
                                                    element: (
                                                        <RouteTrialInstitutionSupportingSiteIdentityProvider>
                                                            <TrialInstitutionSupportingSiteIdentityBreadcrumb />
                                                            <TrialInstitutionSupportingSiteSubMenu />
                                                            <Outlet />
                                                        </RouteTrialInstitutionSupportingSiteIdentityProvider>
                                                    ),
                                                    children: [
                                                        {
                                                            index: true,
                                                            element: (
                                                                <TrialInstitutionSupportingSitePageRedirect />
                                                            )
                                                        },
                                                        {
                                                            path: "details",
                                                            element: (
                                                                <TrialInstitutionSupportingSitePage />
                                                            )
                                                        },
                                                        {
                                                            path: "personnel",
                                                            children: [
                                                                {
                                                                    index: true,
                                                                    element: (
                                                                        <TrialInstitutionSupportingSitePersonnelPage />
                                                                    )
                                                                },
                                                                {
                                                                    path: "create",
                                                                    element: (
                                                                        <TrialInstitutionSupportingSitePersonnelCreatePage />
                                                                    )
                                                                },
                                                                {
                                                                    path: ":personnelRoleId",
                                                                    element: (
                                                                        <TrialInstitutionSupportingSitePersonnelEditPage />
                                                                    )
                                                                }
                                                            ]
                                                        }
                                                    ]
                                                }
                                            ]
                                        },
                                        {
                                            path: "capabilities",
                                            element: <TrialInstitutionCapabilitiesPage />
                                        },
                                        {
                                            path: "emails",
                                            element: <TrialInstitutionEmailsPage />
                                        },
                                        {
                                            path: "participants",
                                            children: [
                                                {
                                                    index: true,
                                                    element: <TrialInstitutionParticipantsPage />
                                                },
                                                {
                                                    path: "create",
                                                    element: <TrialInstitutionParticipantPage />
                                                },
                                                {
                                                    path: ":patientId",
                                                    element: (
                                                        <RoutePatientIdentityProvider>
                                                            <PatientIdentityBreadcrumb />
                                                            <PatientSubMenu />
                                                            <Outlet />
                                                        </RoutePatientIdentityProvider>
                                                    ),
                                                    children: [
                                                        {
                                                            index: true,
                                                            element: (
                                                                <TrialInstitutionParticipantPageRedirect />
                                                            )
                                                        },
                                                        {
                                                            path: "details",
                                                            element: (
                                                                <TrialInstitutionParticipantPage />
                                                            )
                                                        },
                                                        {
                                                            path: "administrationcasereport",
                                                            element: (
                                                                <TrialParticipantAdministrationCaseReportPage />
                                                            )
                                                        },
                                                        {
                                                            path: "emails",
                                                            element: <TrialParticipantEmailsPage />
                                                        },
                                                        {
                                                            path: "items",
                                                            children: [
                                                                {
                                                                    index: true,
                                                                    element: (
                                                                        <PatientItemsOverviewPage />
                                                                    )
                                                                },
                                                                {
                                                                    path: "create",
                                                                    element: <PatientItemPage />
                                                                },
                                                                {
                                                                    path: ":patientItemId",
                                                                    element: (
                                                                        <RoutePatientItemIdentityProvider>
                                                                            <PatientItemIdentityBreadcrumb />
                                                                            <Outlet />
                                                                        </RoutePatientItemIdentityProvider>
                                                                    ),
                                                                    children: [
                                                                        {
                                                                            index: true,
                                                                            element: (
                                                                                <PatientItemPage />
                                                                            )
                                                                        },
                                                                        {
                                                                            path: "entry",
                                                                            element: (
                                                                                <PatientItemVersionEntryPage />
                                                                            )
                                                                        },
                                                                        {
                                                                            path: "versions/:patientItemVersionId/entry",
                                                                            element: (
                                                                                <PatientItemVersionEntryPage />
                                                                            )
                                                                        }
                                                                    ]
                                                                }
                                                            ]
                                                        }
                                                    ]
                                                }
                                            ]
                                        },
                                        {
                                            path: "items",
                                            children: [
                                                {
                                                    index: true,
                                                    element: <TrialInstitutionItemsPage />
                                                },
                                                {
                                                    path: ":trialInstitutionItemId",
                                                    element: (
                                                        <RouteTrialInstitutionItemIdentityProvider>
                                                            <TrialInstitutionItemIdentityBreadcrumb />
                                                            <Outlet />
                                                        </RouteTrialInstitutionItemIdentityProvider>
                                                    ),
                                                    children: [
                                                        {
                                                            index: true,
                                                            element: <TrialInstitutionItemPage />
                                                        },
                                                        {
                                                            path: "entry",
                                                            element: (
                                                                <TrialInstitutionItemVersionEntryPage />
                                                            )
                                                        },
                                                        {
                                                            path: "versions/:trialInstitutionItemVersionId/entry",
                                                            element: (
                                                                <TrialInstitutionItemVersionEntryPage />
                                                            )
                                                        }
                                                    ]
                                                }
                                            ]
                                        },
                                        {
                                            path: "personnel",
                                            children: [
                                                {
                                                    index: true,
                                                    element: <TrialInstitutionPersonnelPage />
                                                },
                                                {
                                                    path: "create",
                                                    element: <TrialInstitutionPersonnelCreatePage />
                                                },
                                                {
                                                    path: ":personnelRoleId",
                                                    element: (
                                                        <TrialInstitutionPersonnelDetailsPage />
                                                    )
                                                }
                                            ]
                                        },
                                        {
                                            path: "notes",
                                            children: [
                                                {
                                                    index: true,
                                                    element: <TrialInstitutionNotesPage />
                                                },
                                                {
                                                    path: ":noteId",
                                                    element: <TrialInstitutionNotePage />
                                                }
                                            ]
                                        }
                                    ]
                                }
                            ]
                        },
                        {
                            path: "trialinstitution",
                            children: [
                                {
                                    path: ":trialInstitutionId",
                                    children: [
                                        {
                                            path: "details",
                                            element: <TrialInstitutionDetailsPageRedirect />
                                        },
                                        {
                                            path: "participants",
                                            children: [
                                                {
                                                    index: true,
                                                    element: (
                                                        <TrialInstitutionParticipantsPageRedirect />
                                                    )
                                                },
                                                {
                                                    path: ":patientId",
                                                    element: (
                                                        <TrialInstitutionParticipantPageRedirect />
                                                    )
                                                }
                                            ]
                                        },
                                        {
                                            path: "items",
                                            children: [
                                                {
                                                    index: true,
                                                    element: <TrialInstitutionItemsPageRedirect />
                                                },
                                                {
                                                    path: ":trialInstitutionItemId",
                                                    element: <TrialInstitutionItemPageRedirect />
                                                }
                                            ]
                                        },
                                        {
                                            path: "personnel",
                                            children: [
                                                {
                                                    index: true,
                                                    element: (
                                                        <TrialInstitutionPersonnelPageRedirect />
                                                    )
                                                },
                                                {
                                                    path: ":personnelRoleId",
                                                    element: (
                                                        <TrialInstitutionPersonnelDetailsPageRedirect />
                                                    )
                                                }
                                            ]
                                        },
                                        {
                                            path: "notes",
                                            children: [
                                                {
                                                    index: true,
                                                    element: <TrialInstitutionNotesPageRedirect />
                                                },
                                                {
                                                    path: ":noteId",
                                                    element: <TrialInstitutionNotePageRedirect />
                                                }
                                            ]
                                        }
                                    ]
                                }
                            ]
                        }
                    ]
                }
            ]
        }
    ];

    return routes;
};

/**
 * Builds the institution route subtree with its existing page, provider and adapter nesting.
 *
 * Postconditions: Returns fresh route objects without navigating or loading lazy page modules.
 * The user argument is currently unused; route construction does not filter access by user.
 */
const createInstitutionRoutes = (_user?: User): RouteObject[] => {
    const routes: RouteObject[] = [
        {
            path: "institutions",
            children: [
                {
                    index: true,
                    element: <InstitutionsPage />
                },
                {
                    path: "create",
                    element: <InstitutionPage />
                },
                {
                    path: ":institutionId",
                    element: (
                        <RouteInstitutionIdentityProvider>
                            <InstitutionIdentityBreadcrumb />
                            <InstitutionSubMenu />
                            <Outlet />
                        </RouteInstitutionIdentityProvider>
                    ),
                    children: [
                        {
                            path: "details",
                            element: <InstitutionPage />
                        },
                        {
                            path: "forms",
                            children: [
                                {
                                    index: true,
                                    element: <InstitutionFormsPage />
                                },
                                {
                                    path: ":institutionFormId",
                                    element: (
                                        <RouteInstitutionFormIdentityProvider>
                                            <InstitutionFormIdentityBreadcrumb />
                                            <Outlet />
                                        </RouteInstitutionFormIdentityProvider>
                                    ),
                                    children: [
                                        {
                                            index: true,
                                            element: <InstitutionFormPage />
                                        },
                                        {
                                            path: "entry",
                                            element: <InstitutionFormEntryPage />
                                        },
                                        {
                                            path: "versions/:institutionFormVersionId/entry",
                                            element: <InstitutionFormEntryPage />
                                        }
                                    ]
                                }
                            ]
                        },
                        {
                            path: "emails",
                            element: <InstitutionEmailsPage />
                        },
                        {
                            path: "notes",
                            children: [
                                {
                                    index: true,
                                    element: <InstitutionNotesPage />
                                },
                                {
                                    path: ":noteId",
                                    element: <InstitutionNotePage />
                                }
                            ]
                        },
                        {
                            path: "personnel",
                            children: [
                                {
                                    index: true,
                                    element: <InstitutionPersonnelPage />
                                },
                                {
                                    path: "create",
                                    element: <InstitutionPersonnelCreatePage />
                                },
                                {
                                    path: ":personnelRoleId",
                                    element: <InstitutionPersonnelEditPage />
                                }
                            ]
                        },
                        {
                            path: "departments",
                            children: [
                                {
                                    index: true,
                                    element: <InstitutionDepartmentsPage />
                                },
                                {
                                    path: "create",
                                    element: <InstitutionDepartmentEditPage />
                                },
                                {
                                    path: ":departmentId",
                                    element: (
                                        <RouteDepartmentIdentityProvider>
                                            <DepartmentIdentityBreadcrumb />
                                            <DepartmentSubMenu />
                                            <Outlet />
                                        </RouteDepartmentIdentityProvider>
                                    ),
                                    children: [
                                        {
                                            index: true,
                                            element: <InstitutionDepartmentPageRedirect />
                                        },
                                        {
                                            path: "details",
                                            element: <InstitutionDepartmentEditPage />
                                        },
                                        {
                                            path: "personnel",
                                            element: <InstitutionDepartmentPersonnelPage />
                                        },
                                        {
                                            path: "personnel/create",
                                            element: <InstitutionDepartmentPersonnelCreatePage />
                                        },
                                        {
                                            path: "personnel/:personnelRoleId",
                                            element: <InstitutionDepartmentPersonnelEditPage />
                                        }
                                    ]
                                }
                            ]
                        }
                    ]
                }
            ]
        }
    ];

    return routes;
};

/**
 * Builds the group route subtree with its existing page, provider and adapter nesting.
 *
 * Postconditions: Returns fresh route objects without navigating or loading lazy page modules.
 * The user argument is currently unused; route construction does not filter access by user.
 */
const createGroupRoutes = (_user?: User): RouteObject[] => {
    const routes: RouteObject[] = [
        {
            path: "groups",
            children: [
                {
                    index: true,
                    element: <GroupsPage />
                },
                {
                    path: ":groupId",
                    element: (
                        <RouteGroupIdentityProvider>
                            <GroupIdentityBreadcrumb />
                            <GroupSubMenu />
                            <Outlet />
                        </RouteGroupIdentityProvider>
                    ),
                    children: [
                        {
                            path: "details",
                            element: <GroupPage />
                        },
                        {
                            path: "notes",
                            children: [
                                {
                                    index: true,
                                    element: <GroupNotesPage />
                                },
                                {
                                    path: ":noteId",
                                    element: <GroupNotePage />
                                }
                            ]
                        },
                        {
                            path: "personnel",
                            children: [
                                {
                                    index: true,
                                    element: <GroupPersonnelPage />
                                },
                                {
                                    path: "create",
                                    element: <GroupPersonnelCreatePage />
                                },
                                {
                                    path: ":personnelRoleId",
                                    element: <GroupPersonnelEditPage />
                                }
                            ]
                        },
                        {
                            path: "trials",
                            element: <GroupTrialsPage />
                        },
                        {
                            path: "configuration",
                            element: (
                                <>
                                    <GroupConfigurationSubMenu />
                                    <Outlet />
                                </>
                            ),
                            children: [
                                { index: true, element: <GroupConfigurationPage /> },
                                {
                                    path: "items",
                                    children: [
                                        { index: true, element: <TrialGroupItemTemplatesPage /> },
                                        {
                                            path: "import",
                                            element: (
                                                <TrialGroupItemTemplateImportPage scope="group" />
                                            )
                                        },
                                        { path: "create", element: <TrialGroupItemTemplatePage /> },
                                        {
                                            path: ":trialGroupItemTemplateId",
                                            element: <TrialGroupItemTemplatePage />
                                        }
                                    ]
                                }
                            ]
                        },
                        {
                            path: "items",
                            children: [
                                { index: true, element: <TrialGroupItemTemplateLegacyRedirect /> },
                                {
                                    path: ":trialGroupItemTemplateId",
                                    element: <TrialGroupItemTemplateLegacyRedirect />
                                }
                            ]
                        }
                    ]
                }
            ]
        }
    ];

    return routes;
};

/**
 * Builds the personnel route subtree with its existing page, provider and adapter nesting.
 *
 * Postconditions: Returns fresh route objects without navigating or loading lazy page modules.
 * The user argument is currently unused; route construction does not filter access by user.
 */
const createPersonnelRoutes = (_user?: User): RouteObject[] => {
    const routes: RouteObject[] = [
        {
            path: "personnel",
            children: [
                {
                    index: true,
                    element: <PersonnelPage />
                },
                {
                    path: "byroles",
                    element: <PersonnelByRolesPage />
                },
                {
                    path: "create",
                    element: <PersonnelDetailsPage />
                },
                {
                    path: ":personnelId",
                    element: (
                        <RoutePersonnelIdentityProvider>
                            <PersonnelIdentityBreadcrumb />
                            <PersonnelSubMenu />
                            <Outlet />
                        </RoutePersonnelIdentityProvider>
                    ),
                    children: [
                        {
                            path: "details",
                            element: <PersonnelDetailsPage />
                        },
                        {
                            path: "notes",
                            children: [
                                {
                                    index: true,
                                    element: <PersonnelNotesPage />
                                },
                                {
                                    path: ":noteId",
                                    element: <PersonnelNotePage />
                                }
                            ]
                        },
                        {
                            path: "roles",
                            children: [
                                {
                                    index: true,
                                    element: <PersonnelRolesPage />
                                },
                                {
                                    path: ":personnelRoleId",
                                    element: <PersonnelRoleEditPage />
                                }
                            ]
                        },
                        {
                            path: "activedirectory",
                            element: <PersonnelActiveDirectoryPage />
                        },
                        {
                            path: "openclinica",
                            element: <PersonnelOCRolesPage />
                        },
                        {
                            path: "permissions",
                            element: <PersonnelPermissionsPage />
                        },
                        {
                            path: "files",
                            element: <PersonnelFilesPage />
                        },
                        {
                            path: "items",
                            element: (
                                <Navigate
                                    to="../files"
                                    replace
                                />
                            )
                        },
                        {
                            path: "hubspot",
                            element: <PersonnelHubspotPage />
                        },
                        {
                            path: "emails",
                            element: <PersonnelEmailPreferencesPage />
                        }
                    ]
                }
            ]
        }
    ];

    return routes;
};

/**
 * Builds the settings route subtree with its existing page, provider and adapter nesting.
 *
 * Postconditions: Returns fresh route objects without navigating or loading lazy page modules.
 * The user argument is currently unused; route construction does not filter access by user.
 */
const createSettingsRoutes = (_user?: User): RouteObject[] => {
    const routes: RouteObject[] = [
        {
            path: "settings",
            element: (
                <>
                    <SettingsSubMenu />
                    <Outlet />
                </>
            ),
            children: [
                {
                    index: true,
                    element: <SettingsNavigationPage sectionCode="settings" />
                },
                {
                    path: "general",
                    element: (
                        <>
                            <GeneralSettingsSubMenu />
                            <Outlet />
                        </>
                    ),
                    children: [
                        {
                            index: true,
                            element: <SettingsNavigationPage sectionCode="generalsettings" />
                        },
                        {
                            path: "countries",
                            children: [
                                {
                                    index: true,
                                    element: <CountriesPage />
                                },
                                {
                                    path: "create",
                                    element: <CountryPage />
                                },
                                {
                                    path: ":countryId",
                                    element: <CountryPage />
                                }
                            ]
                        },
                        {
                            path: "states",
                            children: [
                                {
                                    index: true,
                                    element: <StatesPage />
                                },
                                {
                                    path: ":stateId",
                                    element: <StatePage />
                                }
                            ]
                        },
                        {
                            path: "itemcomponenttypes",
                            children: [
                                {
                                    index: true,
                                    element: <ItemComponentTypesPage />
                                },
                                {
                                    path: ":itemComponentTypeId",
                                    element: <ItemComponentTypePage />
                                }
                            ]
                        },
                        {
                            path: "notetypes",
                            children: [
                                {
                                    index: true,
                                    element: <NoteTypesPage />
                                },
                                {
                                    path: ":noteTypeId",
                                    element: <NoteTypePage />
                                }
                            ]
                        },
                        {
                            path: "genders",
                            children: [
                                {
                                    index: true,
                                    element: <GendersPage />
                                },
                                {
                                    path: ":genderId",
                                    element: <GenderPage />
                                }
                            ]
                        },
                        {
                            path: "interests",
                            children: [
                                {
                                    index: true,
                                    element: <InterestsPage />
                                },
                                {
                                    path: ":interestId",
                                    element: <InterestPage />
                                }
                            ]
                        },
                        {
                            path: "membershiptypes",
                            children: [
                                {
                                    index: true,
                                    element: <MembershipTypesPage />
                                },
                                {
                                    path: ":membershipTypeId",
                                    element: <MembershipTypePage />
                                }
                            ]
                        },
                        {
                            path: "professionaldisciplines",
                            children: [
                                {
                                    index: true,
                                    element: <ProfessionalDisciplinesPage />
                                },
                                {
                                    path: ":professionalDisciplineId",
                                    element: <ProfessionalDisciplinePage />
                                }
                            ]
                        },
                        {
                            path: "salutations",
                            children: [
                                {
                                    index: true,
                                    element: <SalutationsPage />
                                },
                                {
                                    path: ":salutationId",
                                    element: <SalutationPage />
                                }
                            ]
                        },
                        {
                            path: "roles",
                            children: [
                                {
                                    index: true,
                                    element: <RolesPage />
                                },
                                {
                                    path: "create",
                                    element: <RolePage />
                                },
                                {
                                    path: ":roleId",
                                    element: (
                                        <RouteRoleIdentityProvider>
                                            <RoleIdentityBreadcrumb />
                                            <RoleSubMenu />
                                            <Outlet />
                                        </RouteRoleIdentityProvider>
                                    ),
                                    children: [
                                        {
                                            index: true,
                                            element: (
                                                <Navigate
                                                    to="details"
                                                    replace
                                                />
                                            )
                                        },
                                        {
                                            path: "details",
                                            element: <RolePage />
                                        },
                                        {
                                            path: "permissions",
                                            element: <RolePermissionsPage />
                                        },
                                        {
                                            path: "permissions/create",
                                            element: <RolePermissionsCreatePage />
                                        },
                                        {
                                            path: "permissions/:permissionRequirementRoleId",
                                            element: <RolePermissionPage />
                                        }
                                    ]
                                }
                            ]
                        },
                        {
                            path: "notices",
                            children: [
                                {
                                    index: true,
                                    element: <NoticesPage />
                                },
                                {
                                    path: "create",
                                    element: <NoticePage />
                                },
                                {
                                    path: ":noticeId",
                                    element: <NoticePage />
                                }
                            ]
                        },
                        {
                            path: "emails",
                            element: <GlobalEmailDefaultsPage />
                        },
                        {
                            path: "help",
                            element: <HelpCoveragePage />
                        }
                    ]
                },
                {
                    path: "hubspot",
                    element: (
                        <>
                            <HubspotSettingsSubMenu />
                            <Outlet />
                        </>
                    ),
                    children: [
                        {
                            index: true,
                            element: <SettingsNavigationPage sectionCode="hubspotsettings" />
                        },
                        {
                            path: "activemembers",
                            element: <HubspotActiveMembersPage />
                        },
                        {
                            path: "import",
                            element: <HubspotExportPage />
                        },
                        {
                            path: "emailupdate",
                            element: <HubspotMemberPage />
                        }
                    ]
                },
                {
                    path: "openclinicauserroles",
                    element: <OpenClinicaUserRolesPage />
                },
                {
                    path: "institution",
                    element: (
                        <>
                            <InstitutionSettingsSubMenu />
                            <Outlet />
                        </>
                    ),
                    children: [
                        {
                            index: true,
                            element: <SettingsNavigationPage sectionCode="institutionsettings" />
                        },
                        {
                            path: "sitelocations",
                            children: [
                                {
                                    index: true,
                                    element: <SiteLocationsPage />
                                },
                                {
                                    path: ":siteLocationId",
                                    element: <SiteLocationPage />
                                }
                            ]
                        },
                        {
                            path: "facilityalliancemembers",
                            children: [
                                {
                                    index: true,
                                    element: <FacilityAllianceMembersPage />
                                },
                                {
                                    path: ":facilityAllianceMemberId",
                                    element: <FacilityAllianceMemberPage />
                                }
                            ]
                        },
                        {
                            path: "institutiontypes",
                            children: [
                                {
                                    index: true,
                                    element: <InstitutionTypesPage />
                                },
                                {
                                    path: ":institutionTypeId",
                                    element: <InstitutionTypePage />
                                }
                            ]
                        },
                        {
                            path: "capability-categories",
                            children: [
                                { index: true, element: <InstitutionCapabilityCategoriesPage /> },
                                { path: "create", element: <InstitutionCapabilityCategoryPage /> },
                                {
                                    path: ":institutionCapabilityCategoryId",
                                    element: <InstitutionCapabilityCategoryPage />
                                }
                            ]
                        },
                        {
                            path: "capabilities",
                            children: [
                                { index: true, element: <InstitutionCapabilitiesPage /> },
                                { path: "create", element: <InstitutionCapabilityPage /> },
                                {
                                    path: ":institutionCapabilityId",
                                    element: <InstitutionCapabilityPage />
                                }
                            ]
                        },
                        {
                            path: "tag-categories",
                            children: [
                                { index: true, element: <InstitutionTagCategoriesPage /> },
                                { path: "create", element: <InstitutionTagCategoryPage /> },
                                {
                                    path: ":institutionTagCategoryId",
                                    element: <InstitutionTagCategoryPage />
                                }
                            ]
                        },
                        {
                            path: "tags",
                            children: [
                                { index: true, element: <InstitutionTagsPage /> },
                                { path: "create", element: <InstitutionTagPage /> },
                                { path: ":institutionTagId", element: <InstitutionTagPage /> }
                            ]
                        },
                        {
                            path: "institutioncodings",
                            children: [
                                {
                                    index: true,
                                    element: <InstitutionCodingsPage />
                                },
                                {
                                    path: ":institutionCodingId",
                                    element: <InstitutionCodingPage />
                                }
                            ]
                        }
                    ]
                },
                {
                    path: "participant",
                    element: (
                        <>
                            <ParticipantSettingsSubMenu />
                            <Outlet />
                        </>
                    ),
                    children: [
                        {
                            index: true,
                            element: <SettingsNavigationPage sectionCode="participantsettings" />
                        },
                        {
                            path: "tag-categories",
                            children: [
                                { index: true, element: <PatientTagCategoriesPage /> },
                                { path: "create", element: <PatientTagCategoryPage /> },
                                {
                                    path: ":patientTagCategoryId",
                                    element: <PatientTagCategoryPage />
                                }
                            ]
                        },
                        {
                            path: "tags",
                            children: [
                                { index: true, element: <PatientTagsPage /> },
                                { path: "create", element: <PatientTagPage /> },
                                { path: ":patientTagId", element: <PatientTagPage /> }
                            ]
                        }
                    ]
                },
                {
                    path: "trial",
                    element: (
                        <>
                            <TrialSettingsSubMenu />
                            <Outlet />
                        </>
                    ),
                    children: [
                        {
                            index: true,
                            element: <SettingsNavigationPage sectionCode="trialsettings" />
                        },
                        {
                            path: "trialcategorys",
                            children: [
                                {
                                    index: true,
                                    element: <TrialCategoriesPage />
                                },
                                {
                                    path: ":trialCategoryId",
                                    element: <TrialCategoryPage />
                                }
                            ]
                        },
                        {
                            path: "trialtypes",
                            children: [
                                {
                                    index: true,
                                    element: <TrialTypesPage />
                                },
                                {
                                    path: ":trialTypeId",
                                    element: <TrialTypePage />
                                }
                            ]
                        },
                        {
                            path: "trialphases",
                            children: [
                                {
                                    index: true,
                                    element: <TrialPhasesPage />
                                },
                                {
                                    path: ":trialPhaseId",
                                    element: <TrialPhasePage />
                                }
                            ]
                        },
                        {
                            path: "tumourgroups",
                            children: [
                                {
                                    index: true,
                                    element: <TumourGroupsPage />
                                },
                                {
                                    path: ":tumourGroupId",
                                    element: <TumourGroupPage />
                                }
                            ]
                        },
                        {
                            path: "trialPresentationTypes",
                            children: [
                                {
                                    index: true,
                                    element: <TrialPresentationTypesPage />
                                },
                                {
                                    path: ":trialPresentationTypeId",
                                    element: <TrialPresentationTypePage />
                                }
                            ]
                        },
                        {
                            path: "trialPublicationTypes",
                            children: [
                                {
                                    index: true,
                                    element: <TrialPublicationTypesPage />
                                },
                                {
                                    path: ":trialPublicationTypeId",
                                    element: <TrialPublicationTypePage />
                                }
                            ]
                        },
                        {
                            path: "trialAnalysisTypes",
                            children: [
                                {
                                    index: true,
                                    element: <TrialAnalysisTypesPage />
                                },
                                {
                                    path: ":trialAnalysisTypeId",
                                    element: <TrialAnalysisTypePage />
                                }
                            ]
                        },
                        {
                            path: "involvements",
                            children: [
                                {
                                    index: true,
                                    element: <InvolvementsPage />
                                },
                                {
                                    path: ":involvementId",
                                    element: <InvolvementPage />
                                }
                            ]
                        },
                        {
                            path: "allocatedwps",
                            children: [
                                {
                                    index: true,
                                    element: <AllocatedWPsPage />
                                },
                                {
                                    path: ":allocatedWPId",
                                    element: <AllocatedWPPage />
                                }
                            ]
                        }
                    ]
                },
                {
                    path: "qa",
                    element: (
                        <>
                            <QaSettingsSubMenu />
                            <Outlet />
                        </>
                    ),
                    children: [
                        {
                            index: true,
                            element: <SettingsNavigationPage sectionCode="qasettings" />
                        },
                        {
                            path: "formtemplates",
                            children: [
                                {
                                    index: true,
                                    element: <FormTemplatesPage />
                                },
                                {
                                    path: ":formTemplateId",
                                    element: <FormTemplatePage />
                                }
                            ]
                        },
                        {
                            path: "program-checklist",
                            element: <QaProgramChecklistPage />
                        },
                        {
                            path: "qaEmailTemplates",
                            children: [
                                {
                                    index: true,
                                    element: <QAEmailTemplatesPage />
                                },
                                {
                                    path: ":standardEmailTemplateId",
                                    element: <QAEmailTemplatePage />
                                }
                            ]
                        },
                        {
                            path: "formcomponents",
                            children: [
                                {
                                    index: true,
                                    element: <QAItemComponentsPage />
                                },
                                {
                                    path: ":itemComponentId",
                                    element: (
                                        <RouteItemComponentIdentityProvider>
                                            <ItemComponentIdentityBreadcrumb />
                                            <Outlet />
                                        </RouteItemComponentIdentityProvider>
                                    ),
                                    children: [
                                        {
                                            index: true,
                                            element: <QAItemComponentPage />
                                        },
                                        {
                                            path: ":cloneId",
                                            element: <QAItemComponentPage />
                                        }
                                    ]
                                }
                            ]
                        },
                        {
                            path: "reportingTagCategories",
                            children: [
                                {
                                    index: true,
                                    element: <ReportingTagCategoriesPage />
                                },
                                {
                                    path: ":reportingTagCategoryId",
                                    element: <ReportingTagCategoryPage />
                                }
                            ]
                        },
                        {
                            path: "reportingTags",
                            children: [
                                {
                                    index: true,
                                    element: <ReportingTagsPage />
                                },
                                {
                                    path: ":reportingTagId",
                                    element: <ReportingTagPage />
                                }
                            ]
                        },
                        {
                            path: "taskTypes",
                            children: [
                                {
                                    index: true,
                                    element: <TaskTypesPage />
                                },
                                {
                                    path: ":taskTypeId",
                                    element: <TaskTypePage />
                                }
                            ]
                        },
                        {
                            path: "trackerTypes",
                            children: [
                                {
                                    index: true,
                                    element: <TrackerTypesPage />
                                },
                                {
                                    path: ":trackerTypeId",
                                    element: <TrackerTypePage />
                                }
                            ]
                        },
                        {
                            path: "trackerMilestones",
                            children: [
                                {
                                    index: true,
                                    element: <TrackerMilestonesPage />
                                },
                                {
                                    path: ":trackerMilestoneId",
                                    element: <TrackerMilestonePage />
                                }
                            ]
                        },
                        {
                            path: "formTemplateFileTypes",
                            children: [
                                {
                                    index: true,
                                    element: <FormTemplateFileTypesPage />
                                },
                                {
                                    path: ":formTemplateFileTypeId",
                                    element: <FormTemplateFileTypePage />
                                }
                            ]
                        }
                    ]
                },
                {
                    path: "item",
                    element: (
                        <>
                            <ItemSettingsSubMenu />
                            <Outlet />
                        </>
                    ),
                    children: [
                        {
                            index: true,
                            element: <SettingsNavigationPage sectionCode="itemsettings" />
                        },
                        {
                            path: "itemcomponents",
                            children: [
                                {
                                    index: true,
                                    element: <ItemComponentsPage />
                                },
                                {
                                    path: ":itemComponentId",
                                    element: (
                                        <RouteItemComponentIdentityProvider>
                                            <ItemComponentIdentityBreadcrumb />
                                            <Outlet />
                                        </RouteItemComponentIdentityProvider>
                                    ),
                                    children: [
                                        {
                                            index: true,
                                            element: <ItemComponentPage />
                                        },
                                        {
                                            path: ":cloneId",
                                            element: <ItemComponentPage />
                                        }
                                    ]
                                }
                            ]
                        },
                        {
                            path: "itemcategories",
                            children: [
                                {
                                    index: true,
                                    element: <CategoriesPage />
                                },
                                {
                                    path: ":categoryId",
                                    element: <CategoryPage />
                                }
                            ]
                        },
                        {
                            path: "itemtemplates",
                            children: [
                                {
                                    index: true,
                                    element: <TrialInstitutionItemTemplatesPage />
                                },
                                {
                                    path: ":trialInstitutionItemTemplateId",
                                    element: <TrialInstitutionItemTemplatePage />
                                }
                            ]
                        },
                        {
                            path: "participant-item-templates",
                            children: [
                                { index: true, element: <GlobalPatientItemTemplatesPage /> },
                                { path: "create", element: <GlobalPatientItemTemplatePage /> },
                                {
                                    path: ":patientItemTemplateId",
                                    element: <GlobalPatientItemTemplatePage />
                                }
                            ]
                        },
                        {
                            path: "group-item-templates",
                            children: [
                                {
                                    index: true,
                                    element: <TrialGroupItemTemplatesPage scope="global" />
                                },
                                {
                                    path: "create",
                                    element: <TrialGroupItemTemplatePage scope="global" />
                                },
                                {
                                    path: ":trialGroupItemTemplateId",
                                    element: <TrialGroupItemTemplatePage scope="global" />
                                }
                            ]
                        },
                        {
                            path: "trial-item-templates",
                            children: [
                                {
                                    index: true,
                                    element: <TrialItemTemplatesPage />
                                },
                                {
                                    path: ":trialItemTemplateId",
                                    element: <TrialItemTemplatePage />
                                }
                            ]
                        }
                    ]
                }
            ]
        }
    ];

    return routes;
};

/**
 * Combines general pages and the feature subtrees in their established matching order.
 *
 * Postconditions: Returns a fresh route list, forwarding the user argument to feature factories.
 */
const createRootRoutes = (user?: User): RouteObject[] => {
    const routes: RouteObject[] = [
        {
            path: "dashboard",
            element: <DashboardPage />
        },
        {
            path: "error",
            children: [
                {
                    index: true,
                    element: <ErrorPage />
                },
                {
                    path: ":errorCode",
                    element: <ErrorPage />
                }
            ]
        },
        {
            path: "emails/:emailLogGuid",
            element: <EmailLogPreviewPage />
        },
        {
            path: "help",
            element: <HelpPage />
        },
        ...createTrialRoutes(user),
        ...createInstitutionRoutes(user),
        ...createGroupRoutes(user),
        ...createPersonnelRoutes(user),
        ...createSettingsRoutes(user),
        {
            path: "*",
            element: <ErrorPage defaultErrorCode="404" />
        }
    ];

    return routes;
};

/** Supplies the shared layout defaults with the main navigation collection. */
const initialLayoutState: ILayoutContext = {
    ...defaultLayoutContext,
    menuItems: mainNavigationItems
};

/**
 * Builds the root route tree inside the layout provider and application shell.
 *
 * Postconditions: The index route requests replacement navigation to dashboard when rendered.
 * Static breadcrumbs and the outlet share the same shell. A navigation resets scroll to the top and
 * back or forward restores the offset that entry was left at. Construction does not authenticate,
 * authorize, navigate or wait for lazy imports; rendering retains those existing responsibilities.
 *
 * Assumptions: The document is the scrolling element in both shells, so window-based restoration
 * applies. Restoring an offset needs the destination rendered at its full height, which a page
 * still loading its data is not; such a restore clamps to the top.
 */
export const createRoutes = (user?: User): RouteObject[] => {
    const routes: RouteObject[] = [
        {
            path: "/",
            element: (
                <LayoutProvider initialState={initialLayoutState}>
                    <ApplicationLayout>
                        {/*
                         * Deliberately without getKey: the default location.key gives every history
                         * entry its own saved offset, so pushing a path visited earlier starts at
                         * the top rather than restoring that earlier visit's position.
                         */}
                        <ScrollRestoration />
                        <StaticRouteBreadcrumbs />
                        <Outlet />
                        {/* <AnalyticsTracker /> */}
                    </ApplicationLayout>
                </LayoutProvider>
            ),
            children: [
                {
                    index: true,
                    element: (
                        <Navigate
                            to="/dashboard"
                            replace
                        />
                    )
                },
                ...createRootRoutes(user)
            ]
        }
    ];

    return routes;
};

export default createRoutes;
